import { apiSlice } from "../../../features/api/apiSlice";

export const upgradeApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------------------------------------------------------------- */
    /* NEW grade-upgrade flow (eligibility → update details → pay)        */
    /* ---------------------------------------------------------------- */

    // Drives the upgrade button: current/target grade, route options,
    // CPD status, form completeness, and payment blockers.
    getUpgradeEligibility: builder.query<any, void>({
      query: () => "/upgrade/eligibility",
      providesTags: ["UpgradeApplications"],
    }),

    // The member's own grade-upgrade applications (created on payment verify).
    getMyGradeUpgrades: builder.query<any[], void>({
      query: () => "/upgrade/applications",
      transformResponse: (res: any) =>
        Array.isArray(res) ? res : res?.data ?? [],
      providesTags: ["UpgradeApplications"],
    }),

    getGradeUpgradeDetails: builder.query<any, string | number>({
      query: (id) => `/upgrade/applications/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "UpgradeApplicationDetails" as const, id: String(id) },
      ],
    }),

    // The post-recommendation bundle: upgrade charge + induction fee + a fee per
    // induction event, itemised. Only payable while the application is
    // 'recommended'; settling it books the events and approves the upgrade.
    getUpgradeInduction: builder.query<any, string | number>({
      query: (id) => `/upgrade/applications/${id}/induction`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_r, _e, id) => [
        { type: "UpgradeApplicationDetails" as const, id: String(id) },
      ],
    }),

    // `guests` are optional extra access cards for non-members — one entry per
    // card. Buying one is optional, but every field is required: each becomes a
    // real event registration in that guest's name. Omit for none.
    initiateUpgradeInductionPayment: builder.mutation<
      any,
      { id: string | number; guests?: { name: string; email: string; phone: string }[] }
    >({
      query: ({ id, guests }) => ({
        url: `/upgrade/applications/${id}/induction/pay`,
        method: "POST",
        body: { guests: guests ?? [] },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "UpgradeApplicationDetails" as const, id: String(id) },
      ],
    }),

    // Returns the OnePay payload, or a 422 with { message, blockers }.
    initiateUpgradePayment: builder.mutation<any, { route?: string } | void>({
      query: (body) => ({
        url: "/upgrade/payment/initiate",
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    /* ---------------------------------------------------------------- */
    /* The 8-step upgrade form (upgrade-owned, separate from probationer) */
    /* ---------------------------------------------------------------- */

    // The member's in-progress draft (with all step relations) — resumes wizard.
    getUpgradeCurrent: builder.query<any, void>({
      query: () => "/upgrade/applications/current",
      providesTags: ["UpgradeApplications"],
    }),

    upgradeStep1: builder.mutation<any, FormData>({
      query: (body) => ({ url: "/upgrade/applications/step1", method: "POST", body }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep2: builder.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({ url: `/upgrade/applications/${id}/step2`, method: "POST", body }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep3: builder.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({ url: `/upgrade/applications/${id}/step3`, method: "POST", body }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep4: builder.mutation<any, { id: string | number; memberships: any[] }>({
      query: ({ id, memberships }) => ({ url: `/upgrade/applications/${id}/step4`, method: "POST", body: { memberships } }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep5: builder.mutation<any, { id: string | number; experiences: any[] }>({
      query: ({ id, experiences }) => ({ url: `/upgrade/applications/${id}/step5`, method: "POST", body: { experiences } }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep6: builder.mutation<any, { id: string | number; seminars: any[] }>({
      query: ({ id, seminars }) => ({ url: `/upgrade/applications/${id}/step6`, method: "POST", body: { seminars } }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeStep7: builder.mutation<any, { id: string | number; referees: any[] }>({
      query: ({ id, referees }) => ({ url: `/upgrade/applications/${id}/step7`, method: "POST", body: { referees } }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    // Fellow-only extra step: papers (14), dissertation (15), benevolence (16).
    // Multipart because the dissertation is a file upload.
    upgradeFellowship: builder.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({ url: `/upgrade/applications/${id}/fellowship`, method: "POST", body }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    // Fellow-only extra step: the one-page attestation and the candidate's
    // current QSRBN practice licence. Multipart — both are file uploads.
    upgradeFellowshipDocuments: builder.mutation<any, { id: string | number; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/upgrade/applications/${id}/fellowship-documents`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    upgradeAcknowledge: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/upgrade/applications/${id}/step8`, method: "POST" }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    // Rejected → reopen the SAME application for editing (no new app, no re-pay).
    upgradeReopen: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/upgrade/applications/${id}/reopen`, method: "POST" }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    // Send an already-paid, reopened application back to review without re-paying.
    upgradeResubmit: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({ url: `/upgrade/applications/${id}/resubmit`, method: "POST" }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    /* ---------------------------------------------------------------- */
    /* Sponsor/Mentor (referee) approval — grade upgrade                  */
    /* ---------------------------------------------------------------- */
    getMyUpgradeRefereeRequests: builder.query<any[], { decision?: string } | void>({
      query: (arg) => {
        const decision = (arg as any)?.decision;
        const qs = decision ? `?decision=${encodeURIComponent(decision)}` : "";
        return { url: `/upgrade/referee/requests${qs}`, method: "GET" };
      },
      transformResponse: (res: any) => {
        if (Array.isArray(res)) return res;
        if (Array.isArray(res?.data)) return res.data;
        return [];
      },
      providesTags: ["UpgradeApplications"],
    }),
    acceptUpgradeRefereeRequest: builder.mutation<any, { refereeId: string; remarks?: string }>({
      query: ({ refereeId, remarks }) => ({
        url: `/upgrade/referee/requests/${refereeId}/accept`,
        method: "POST",
        body: remarks ? { remarks } : {},
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    rejectUpgradeRefereeRequest: builder.mutation<any, { refereeId: string; remarks: string }>({
      query: ({ refereeId, remarks }) => ({
        url: `/upgrade/referee/requests/${refereeId}/reject`,
        method: "POST",
        body: { remarks },
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    /* ---------------------------------------------------------------- */
    /* Admin — grade upgrade review                                       */
    /* ---------------------------------------------------------------- */

    /**
     * The admin listing.
     *
     * Applications that were treated in a CLOSED review cycle are left out by
     * the server — that sitting is where they now live. `include_closed_cycles`
     * asks for them anyway, which the cycles list needs: it counts how much of
     * each grade every cycle holds, and would otherwise read zero for every
     * closed one and then hide it.
     */
    adminGradeUpgrades: builder.query<
      any,
      {
        status?: string;
        page?: number;
        per_page?: number;
        to_category_id?: number | string;
        include_closed_cycles?: boolean;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.status && arg.status !== "all") params.set("status", arg.status);
        if (arg?.page) params.set("page", String(arg.page));
        if (arg?.per_page) params.set("per_page", String(arg.per_page));
        if (arg?.to_category_id) params.set("to_category_id", String(arg.to_category_id));
        if (arg?.include_closed_cycles) params.set("include_closed_cycles", "1");
        const qs = params.toString();
        return `/admin/upgrade-applications${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["UpgradeApplications"],
    }),

    // Pipeline analysis across EVERY upgrade application — not the page the
    // admin is looking at. Aggregated server-side.
    // `payment` narrows the whole sheet to paid / unpaid applications.
    adminUpgradeAnalysis: builder.query<any, { payment?: "all" | "paid" | "unpaid" } | void>({
      query: (arg) => {
        const payment = arg?.payment ?? "all";
        return `/admin/upgrade-applications/analysis${payment !== "all" ? `?payment=${payment}` : ""}`;
      },
      providesTags: ["UpgradeApplications"],
    }),

    /* ── Member numbering ─────────────────────────────────────────────── */

    /**
     * What issuing would do: where the series has got to, who would be numbered
     * and what each would get, plus who would be passed over and why.
     *
     * Read-only, and deliberately fetched before anything is written — the
     * numbers are permanent, so the secretariat sees the list first.
     */
    adminMemberNumberPreview: builder.query<
      any,
      {
        to_category_id?: number | string;
        from_category_id?: number | string;
        /** Narrow the batch to one review cycle's applicants. */
        review_diet_id?: string;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.to_category_id) params.set("to_category_id", String(arg.to_category_id));
        if (arg?.from_category_id) params.set("from_category_id", String(arg.from_category_id));
        if (arg?.review_diet_id) params.set("review_diet_id", String(arg.review_diet_id));
        const qs = params.toString();
        return `/admin/upgrade-applications/member-numbers/preview${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["UpgradeApplications"],
    }),

    /**
     * Issue the batch. Invalidates the applications list, which carries each
     * member's number — the table and the exports must show the new numbers the
     * moment they exist.
     */
    adminIssueMemberNumbers: builder.mutation<
      any,
      {
        to_category_id?: number | string;
        from_category_id?: number | string;
        review_diet_id?: string;
        start_from?: number;
      }
    >({
      query: (body) => ({
        url: "/admin/upgrade-applications/member-numbers/issue",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    /* ── Conferring the grade — the step after numbering ─────────────── */

    /**
     * Who would actually be upgraded, and who would be passed over.
     *
     * A separate act from issuing: numbering puts the new number on the
     * register beside the old one, this makes it the number in force and the
     * person the new grade.
     */
    adminGradeUpgradePreview: builder.query<
      any,
      {
        to_category_id?: number | string;
        from_category_id?: number | string;
        review_diet_id?: string;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg?.to_category_id) params.set("to_category_id", String(arg.to_category_id));
        if (arg?.from_category_id) params.set("from_category_id", String(arg.from_category_id));
        if (arg?.review_diet_id) params.set("review_diet_id", String(arg.review_diet_id));
        const qs = params.toString();
        return `/admin/upgrade-applications/grade-upgrades/preview${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["UpgradeApplications"],
    }),

    /**
     * Confer the grade on the batch.
     *
     * Invalidates the applications list AND the member register: this changes
     * what somebody IS, so a list still showing their old grade would be wrong
     * the moment it finishes.
     */
    adminApplyGradeUpgrade: builder.mutation<
      any,
      {
        to_category_id?: number | string;
        from_category_id?: number | string;
        review_diet_id?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/upgrade-applications/grade-upgrades/apply",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications", "Members"],
    }),

    /* ── Review cycles (diets) ───────────────────────────────────────── */

    /** Every review cycle, newest first. */
    adminUpgradeReviewDiets: builder.query<any, void>({
      query: () => "/admin/upgrade-applications/review-diets",
      providesTags: ["UpgradeReviewDiets"],
    }),

    /** One cycle: its panel, the applications in it, and the pool to pull from. */
    adminUpgradeReviewDiet: builder.query<any, string>({
      query: (id) => `/admin/upgrade-applications/review-diets/${id}`,
      providesTags: (_r, _e, id) => [{ type: "UpgradeReviewDiets" as const, id }],
    }),

    adminCreateUpgradeReviewDiet: builder.mutation<any, Record<string, any>>({
      query: (body) => ({
        url: "/admin/upgrade-applications/review-diets",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeReviewDiets"],
    }),

    adminUpdateUpgradeReviewDiet: builder.mutation<any, { id: string } & Record<string, any>>({
      query: ({ id, ...body }) => ({
        // POST, not PUT — the host blocks the less common verbs.
        url: `/admin/upgrade-applications/review-diets/${id}/update`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "UpgradeReviewDiets" as const, id: arg.id },
        "UpgradeReviewDiets",
      ],
    }),

    /** Refused by the server once the cycle holds applications or reviews. */
    adminDeleteUpgradeReviewDiet: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admin/upgrade-applications/review-diets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UpgradeReviewDiets"],
    }),

    /** Pull applications into a cycle. */
    adminAddUpgradeReviewDietApplications: builder.mutation<
      any,
      { id: string; application_ids: (string | number)[] }
    >({
      query: ({ id, application_ids }) => ({
        url: `/admin/upgrade-applications/review-diets/${id}/applications`,
        method: "POST",
        body: { application_ids },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "UpgradeReviewDiets" as const, id: arg.id },
        "UpgradeReviewDiets",
        "UpgradeApplications",
      ],
    }),

    adminRemoveUpgradeReviewDietApplication: builder.mutation<
      any,
      { id: string; applicationId: string | number }
    >({
      query: ({ id, applicationId }) => ({
        url: `/admin/upgrade-applications/review-diets/${id}/applications/${applicationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "UpgradeReviewDiets" as const, id: arg.id },
        "UpgradeReviewDiets",
        "UpgradeApplications",
      ],
    }),

    /* ── Membership Committee assignment ─────────────────────────────── */

    /** Members currently sitting on the Membership Committee. */
    adminCommitteePool: builder.query<any, void>({
      query: () => "/admin/upgrade-applications/committee/members",
      providesTags: ["UpgradeApplications"],
    }),

    /** Everything the assignment screen needs: members, awaiting, assigned. */
    adminCommitteeQueue: builder.query<any, void>({
      query: () => "/admin/upgrade-applications/committee/queue",
      providesTags: ["UpgradeApplications"],
    }),

    /** Hand a batch of applications to one committee member. */
    adminAssignCommitteeBulk: builder.mutation<
      any,
      { member_id: string; application_ids: (string | number)[] }
    >({
      query: (body) => ({
        url: "/admin/upgrade-applications/committee/assign-bulk",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications", "UpgradeReviewDiets"],
    }),

    /** Who is already reviewing one application, and their decisions. */
    adminCommitteeReviews: builder.query<any, string | number>({
      query: (id) => `/admin/upgrade-applications/${id}/committee/reviews`,
      providesTags: (_r, _e, id) => [{ type: "UpgradeApplications" as const, id: `committee:${id}` }],
    }),

    adminAssignCommittee: builder.mutation<any, { id: string | number; member_ids: string[] }>({
      query: ({ id, member_ids }) => ({
        url: `/admin/upgrade-applications/${id}/committee/assign`,
        method: "POST",
        body: { member_ids },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "UpgradeApplications" as const, id: `committee:${arg.id}` },
        "UpgradeApplications",
      ],
    }),

    adminUnassignCommittee: builder.mutation<any, { id: string | number; reviewId: number }>({
      query: ({ id, reviewId }) => ({
        url: `/admin/upgrade-applications/${id}/committee/reviews/${reviewId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "UpgradeApplications" as const, id: `committee:${arg.id}` },
        "UpgradeApplications",
      ],
    }),

    adminGradeUpgradeDetails: builder.query<any, string | number>({
      query: (id) => `/admin/upgrade-applications/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "UpgradeApplicationDetails" as const, id: String(id) },
      ],
    }),

    updateGradeUpgradeStatus: builder.mutation<
      any,
      { id: string | number; status: string; remark?: string; cpd_override?: boolean }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/upgrade-applications/${id}/status`,
        // POST (not PATCH): some shared hosts block PATCH before it reaches the
        // app, which the browser reports as a CORS/network error. The route
        // accepts both.
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        "UpgradeApplications",
        { type: "UpgradeApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    // Return an unpaid application to draft and clear its payment link.
    adminResetUpgradeToDraft: builder.mutation<any, { id: string | number }>({
      query: ({ id }) => ({
        url: `/admin/upgrade-applications/${id}/reset-to-draft`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, arg) => [
        "UpgradeApplications",
        { type: "UpgradeApplicationDetails" as const, id: String(arg.id) },
      ],
    }),

    /* ---------------------------------------------------------------- */
    /* Admin — record a bank / offline upgrade payment                    */
    /* ---------------------------------------------------------------- */

    // Search members; each match carries its auto-derived upgrade context
    // (current grade, target grade, fee/route_fees) so the level is resolved
    // the same way the member's own application computes it.
    adminUpgradeMemberLookup: builder.query<{ data: any[] }, string>({
      query: (q) => `/admin/upgrade-applications/members/lookup?q=${encodeURIComponent(q)}`,
    }),

    // Record a bank/offline upgrade payment for a member. Sends multipart so the
    // proof file rides along; the browser sets the multipart boundary itself.
    adminRecordUpgradeBankPayment: builder.mutation<any, FormData>({
      query: (body) => ({
        url: "/admin/upgrade-applications/bank-payment",
        method: "POST",
        body,
        formData: true,
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),

    /* ---------------------------------------------------------------- */
    /* Legacy endpoints (firm application + old upgrade tables)           */
    /* ---------------------------------------------------------------- */

    getMyUpgradeApplications: builder.query<any[], void>({
      query: () => "/membership-upgrades",
      providesTags: ["UpgradeApplications"],
    }),
    getMyFellowshipApplications: builder.query<any[], void>({
      query: () => "/fellowship-upgrades",
      providesTags: ["UpgradeApplications"],
    }),
    getUpgradeApplicationDetails: builder.query<any, string>({
      query: (id) => `/membership-upgrades/${id}`,
      providesTags: (_r, _e, id) => [{ type: "UpgradeApplicationDetails" as const, id }],
    }),
    getFellowshipApplicationDetails: builder.query<any, string>({
      query: (id) => `/fellowship-upgrades/${id}`,
      providesTags: (_r, _e, id) => [{ type: "UpgradeApplicationDetails" as const, id }],
    }),
    submitUpgradeApplication: builder.mutation<any, { body: any }>({
      query: ({ body }) => ({
        url: "/membership-upgrades",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    submitFellowshipApplication: builder.mutation<any, { body: any }>({
      query: ({ body }) => ({
        url: "/fellowship-upgrades",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UpgradeApplications"],
    }),
    uploadUpgradeDocument: builder.mutation<{ path: string; url?: string }, FormData>({
      query: (body) => ({
        url: "/upgrade-documents",
        method: "POST",
        body,
        // Don't set Content-Type — browser sets multipart boundary automatically for FormData
        formData: true,
      }),
    }),
    // NOTE: firm-application endpoints moved to modules/firm-application/api/firmApi.ts
  }),
  overrideExisting: false,
});

export const {
  // new grade-upgrade flow
  useGetUpgradeEligibilityQuery,
  useGetMyGradeUpgradesQuery,
  useGetGradeUpgradeDetailsQuery,
  useGetUpgradeInductionQuery,
  useInitiateUpgradeInductionPaymentMutation,
  useInitiateUpgradePaymentMutation,
  // upgrade 8-step form
  useGetUpgradeCurrentQuery,
  useUpgradeStep1Mutation,
  useUpgradeStep2Mutation,
  useUpgradeStep3Mutation,
  useUpgradeStep4Mutation,
  useUpgradeStep5Mutation,
  useUpgradeStep6Mutation,
  useUpgradeStep7Mutation,
  useUpgradeFellowshipMutation,
  useUpgradeFellowshipDocumentsMutation,
  useUpgradeAcknowledgeMutation,
  useUpgradeReopenMutation,
  useUpgradeResubmitMutation,
  useGetMyUpgradeRefereeRequestsQuery,
  useAcceptUpgradeRefereeRequestMutation,
  useRejectUpgradeRefereeRequestMutation,
  // admin
  useAdminGradeUpgradesQuery,
  useAdminUpgradeAnalysisQuery,
  useAdminMemberNumberPreviewQuery,
  useAdminIssueMemberNumbersMutation,
  useAdminGradeUpgradePreviewQuery,
  useAdminApplyGradeUpgradeMutation,
  useAdminUpgradeReviewDietsQuery,
  useAdminUpgradeReviewDietQuery,
  useAdminCreateUpgradeReviewDietMutation,
  useAdminUpdateUpgradeReviewDietMutation,
  useAdminDeleteUpgradeReviewDietMutation,
  useAdminAddUpgradeReviewDietApplicationsMutation,
  useAdminRemoveUpgradeReviewDietApplicationMutation,
  useAdminCommitteePoolQuery,
  useAdminCommitteeQueueQuery,
  useAdminAssignCommitteeBulkMutation,
  useAdminCommitteeReviewsQuery,
  useAdminAssignCommitteeMutation,
  useAdminUnassignCommitteeMutation,
  useAdminGradeUpgradeDetailsQuery,
  useUpdateGradeUpgradeStatusMutation,
  useAdminResetUpgradeToDraftMutation,
  useLazyAdminUpgradeMemberLookupQuery,
  useAdminRecordUpgradeBankPaymentMutation,
  // legacy
  useGetMyUpgradeApplicationsQuery,
  useGetMyFellowshipApplicationsQuery,
  useGetUpgradeApplicationDetailsQuery,
  useGetFellowshipApplicationDetailsQuery,
  useSubmitUpgradeApplicationMutation,
  useSubmitFellowshipApplicationMutation,
  useUploadUpgradeDocumentMutation,
} = upgradeApi;
